import React, { useEffect, useRef, useState } from 'react';
import { Flex, View } from '@smarterservices/smarter-ui';
import PropTypes from 'prop-types';

export default function AudioVisualizer({
  track,
  barCount = 5,
  height = '16px',
  barWidth = '4px',
  activeColor = 'success',
  inactiveColor = 'secondary',
  minBarHeight = '10px',
  silenceThreshold = 0.1, // Threshold below which audio is considered silent
  smoothingFactor = 0.8,   // Smoothing factor for level changes
  withVisualDebug = false,
  showBars = true,
  onSoundDetectedThreshold = 500, // Minimum time in ms between sound detections
  onSoundDetected,          // Callback when sound is first detected (once per track)
}) {
  const [levels, setLevels] = useState(Array(barCount).fill(0));
  const [isSoundDetected, setIsSoundDetected] = useState(false);
  const animationFrameId = useRef();
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef();
  const lastLevels = useRef(Array(barCount).fill(0));
  const noiseFloor = useRef(0.02);
  const dynamicThreshold = useRef(0.1);
  const hasTriggeredDetection = useRef(false); // Track if we've triggered the callback for current track
  const lastCallbackTime = useRef(0); // Track last callback time for throttling

  // Set up audio analysis when track changes
  useEffect(() => {
    if (!track) {
      console.log('AudioVisualizer: No track provided');
      setLevels(Array(barCount).fill(0));
      return;
    }
    /*
    console.log('AudioVisualizer: New track received', {
      id: track.id,
      kind: track.kind,
      label: track.label,
      muted: track.muted,
      readyState: track.readyState,
      enabled: track.enabled
    });
    */
    
    // Reset levels and detection state when track changes
    setLevels(Array(barCount).fill(0.1)); // Start with small visible bars
    hasTriggeredDetection.current = false; // Reset the detection flag for new track
    lastCallbackTime.current = 0; // Reset the last callback time

    let audioContext;
    let source;
    let analyser;

    const initAudio = async () => {
      try {
        // Create audio context and nodes
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        audioContext = new AudioContext();
        audioContextRef.current = audioContext;

        analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.5; // Smoother transitions
        analyserRef.current = analyser;

        // Create stream source from track
        const stream = new MediaStream([track]);
        source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        // Initialize data array for frequency data
        const bufferLength = analyser.frequencyBinCount;
        dataArrayRef.current = new Uint8Array(bufferLength);

        // Start animation loop
        const updateVisualizer = () => {
          if (!analyserRef.current) return;
          
          // Get frequency data
          analyser.getByteFrequencyData(dataArrayRef.current);
          const data = dataArrayRef.current;
          
          // Process frequency bands into bars
          const newLevels = [];
          const blockSize = Math.floor(data.length / barCount);
          let totalEnergy = 0;
          
          // Calculate a noise floor based on the quietest frequency band
          let minLevel = 1;
          const rawLevels = [];
          
          // First pass: calculate raw levels and find minimum
          for (let i = 0; i < barCount; i++) {
            let sum = 0;
            for (let j = 0; j < blockSize; j++) {
              sum += data[i * blockSize + j] || 0;
            }
            const avg = sum / blockSize / 255; // Normalize to 0-1
            rawLevels.push(avg);
            if (avg > 0.001) { // Ignore values too close to zero
              minLevel = Math.min(minLevel, avg);
            }
          }
          
          // Calculate noise floor (slightly above the minimum level)
          const noiseFloor = Math.max(0.01, minLevel * 1.5);
          
          // Second pass: apply noise reduction and scaling
          for (let i = 0; i < barCount; i++) {
            let level = rawLevels[i];
            
            // Apply noise reduction
            level = Math.max(0, level - noiseFloor) / (1 - noiseFloor);
            
            // Apply frequency-specific adjustments
            if (i < 2) {
              level *= 0.7; // Reduce low frequency sensitivity
            }
            
            // Apply exponential scaling
            const scaledLevel = Math.pow(level, 0.7);
            
            // Apply dynamic range compression
            const compressedLevel = 1 - Math.exp(-3 * scaledLevel);
            
            // Smooth with previous value
            const lastLevel = lastLevels.current[i] || 0.01;
            const smoothing = i < 2 ? 0.5 : 0.4; // More smoothing for low frequencies
            const smoothedLevel = lastLevel + (compressedLevel - lastLevel) * smoothing;
            
            newLevels.push(smoothedLevel);
            totalEnergy += smoothedLevel;
          }
          
          // Update sound detection state with some hysteresis
          const averageEnergy = totalEnergy / barCount;
          const soundDetected = isSoundDetected
            ? averageEnergy > (silenceThreshold * 0.8)  // Lower threshold to turn off
            : averageEnergy > silenceThreshold;         // Normal threshold to turn on
          
          // Handle sound detection changes
          if (soundDetected !== isSoundDetected) {
            const now = Date.now();
            const timeSinceLastCallback = now - lastCallbackTime.current;
            
            if (soundDetected && onSoundDetected) {
              if (timeSinceLastCallback >= onSoundDetectedThreshold) {
                // Call the callback with averageEnergy
                onSoundDetected({averageEnergy});
                lastCallbackTime.current = now;
                hasTriggeredDetection.current = true;
              }
            }
            
            // Log sound information (throttled)
            /*
            if (now % 1000 < 16) { // ~once per second
              console.log(`Sound ${soundDetected ? 'detected' : 'stopped'}`, {
                averageEnergy: averageEnergy.toFixed(4),
                threshold: silenceThreshold,
                levels: newLevels.map(lvl => lvl.toFixed(4)),
                isSoundDetected: soundDetected
              });
            }
            */
          }
         /* 
          // Log detailed sound info every second (throttled)
          if (Date.now() % 1000 < 16) { // ~once per second
            console.debug('Audio levels:', {
              timestamp: Date.now(),
              averageEnergy: averageEnergy.toFixed(4),
              maxLevel: Math.max(...newLevels).toFixed(4),
              minLevel: Math.min(...newLevels).toFixed(4)
            });
          }
          */
          setIsSoundDetected(soundDetected);
          
          // Update levels if changed
          setLevels(newLevels);
          lastLevels.current = newLevels;
          
          // Continue animation loop
          animationFrameId.current = requestAnimationFrame(updateVisualizer);
        };
        
        updateVisualizer();
        
      } catch (error) {
        console.error('Error initializing audio analysis:', error);
      }
    };

    initAudio();

    // Cleanup function
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      if (source) {
        source.disconnect();
      }
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close();
      }
    };
  }, [track, barCount, smoothingFactor, silenceThreshold]);

  // If no track, show minimal bars
  if (!track) {
    return (
      <Flex
        as="div"
        alignItems="center"
        justifyContent="center"
        height={height}
      >
        {Array(barCount).fill(0).map((_, i) => (
          <View
            key={i}
            as="div"
            width={barWidth}
            height={minBarHeight}
            backgroundColor={inactiveColor}
            borderRadius="small"
            margin="0 x-small 0 0"
          />
        ))}
      </Flex>
    );
  }

  return (
    showBars &&
    <View as="div" withVisualDebug={withVisualDebug}>
      <Flex 
        as="div"
        alignItems="center"
        justifyContent="center"
        height={height}
        gap="x-small"
      >
        {levels.map((level, i) => {
          // Scale the level for better visualization
          const scaledLevel = Math.pow(level, 0.7); // Apply power curve for better visibility
          const barHeight = `${Math.max(scaledLevel * 100, 5)}%`;
          // Use the individual bar's level to determine if it's active
          const isBarActive = level > silenceThreshold;
          
          return (
            <View
              withVisualDebug={withVisualDebug}
              key={i}
              as="div"
              width={barWidth}
              height={barHeight}
              background={isBarActive ? activeColor : inactiveColor}
              borderRadius="small"
              transition="all 0.1s ease"
              position="relative"
              style={{
                minHeight: minBarHeight,
                transition: 'all 0.1s ease-out',
              }}
            />
          );
        })}
      </Flex>
    </View>
  );
}
